import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from 'sonner';

// Context
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';

// Components
import ProtectedRoute from './components/ProtectedRoute';
import UserDashboardWrapper from './components/UserDashboardWrapper';
import AdminDashboardWrapper from './components/AdminDashboardWrapper';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import AdminDashboard from './pages/AdminDashboard';
import AssessmentFlow from './pages/AssessmentFlow';
import ResultsPage from './pages/ResultsPage';
import AdminPrompts from './pages/AdminPrompts';
import BookingPage from './pages/BookingPage';
import AdminBookings from './pages/AdminBookings';
import ApplicationPage from './pages/ApplicationPage';
import AdminApplications from './pages/AdminApplications';
import AdminEmailTemplates from './pages/AdminEmailTemplates';
import AdminEmailSequences from './pages/AdminEmailSequences';
import AdminEmailLogs from './pages/AdminEmailLogs';
import AdminParticipants from './pages/AdminParticipants';
import AdminParticipantDetail from './pages/AdminParticipantDetail';
import AdminSettings from './pages/AdminSettings';
import UserDashboardHome from './pages/UserDashboardHome';
import UserAssessments from './pages/UserAssessments';
import AssessmentComparison from './pages/AssessmentComparison';
import UserSettings from './pages/UserSettings';
import FacilitatorDashboard from './pages/FacilitatorDashboard';
import FacilitatedSession from './pages/FacilitatedSession';
import FacilitatorDashboardWrapper from './components/FacilitatorDashboardWrapper';
import AdminAIInsights from './pages/AdminAIInsights';
import AdminCohorts from './pages/AdminCohorts';
import AdminCohortDetail from './pages/AdminCohortDetail';
import AdminTestimonials from './pages/AdminTestimonials';
import TestimonialSubmit from './pages/TestimonialSubmit';
import AdminActivityLogs from './pages/AdminActivityLogs';
import AdminWebhooks from './pages/AdminWebhooks';
import AdminContentLibrary from './pages/AdminContentLibrary';
import AdminPdfTemplates from './pages/AdminPdfTemplates';
import AdminFlaggedSessions from './pages/AdminFlaggedSessions';
import AdminSessionTemplates from './pages/AdminSessionTemplates';
import FacilitatorAnalytics from './pages/FacilitatorAnalytics';
import ContentLibrary from './pages/ContentLibrary';
import ContentDetail from './pages/ContentDetail';
import NotificationsPage from './pages/NotificationsPage';
import AdminReports from './pages/AdminReports';
import AdminCohortComparison from './pages/AdminCohortComparison';
import AdminParticipantJourney from './pages/AdminParticipantJourney';
import AdminNotifications from './pages/AdminNotifications';

import './App.css';

// Configure axios - use relative URLs so requests go to the same origin
// This ensures /api/* calls work correctly via the Kubernetes ingress
// regardless of which domain the frontend is accessed from
axios.defaults.baseURL = '';
axios.defaults.withCredentials = true;

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <Toaster position="top-right" richColors />
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          
          {/* Redirect old assessment landing pages to main landing */}
          <Route path="/fear-assessment" element={<Navigate to="/" replace />} />
          <Route path="/archetype-assessment" element={<Navigate to="/" replace />} />
          
          {/* Public Testimonial Submission */}
          <Route path="/testimonial/submit/:token" element={<TestimonialSubmit />} />

          {/* Protected Assessment Routes - Full screen experience */}
          <Route
            path="/assessment/:type/start"
            element={
              <ProtectedRoute>
                <AssessmentFlow />
              </ProtectedRoute>
            }
          />
          <Route
            path="/assessment/:type/:assessmentId"
            element={
              <ProtectedRoute>
                <AssessmentFlow />
              </ProtectedRoute>
            }
          />
          <Route
            path="/results/:type/:id"
            element={
              <ProtectedRoute>
                <UserDashboardWrapper><ResultsPage /></UserDashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/results/:id"
            element={
              <ProtectedRoute>
                <UserDashboardWrapper><ResultsPage /></UserDashboardWrapper>
              </ProtectedRoute>
            }
          />

          {/* Protected User Dashboard Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <UserDashboardWrapper><UserDashboardHome /></UserDashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/home"
            element={
              <ProtectedRoute>
                <UserDashboardWrapper><UserDashboardHome /></UserDashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/assessments"
            element={
              <ProtectedRoute>
                <UserDashboardWrapper><UserAssessments /></UserDashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/compare"
            element={
              <ProtectedRoute>
                <UserDashboardWrapper><AssessmentComparison /></UserDashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/settings"
            element={
              <ProtectedRoute>
                <UserDashboardWrapper><UserSettings /></UserDashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/notifications"
            element={
              <ProtectedRoute>
                <UserDashboardWrapper><NotificationsPage /></UserDashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/book"
            element={
              <ProtectedRoute>
                <UserDashboardWrapper><BookingPage /></UserDashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/apply"
            element={
              <ProtectedRoute>
                <UserDashboardWrapper><ApplicationPage /></UserDashboardWrapper>
              </ProtectedRoute>
            }
          />
          
          {/* Content Library (Participant) */}
          <Route
            path="/library"
            element={
              <ProtectedRoute>
                <UserDashboardWrapper><ContentLibrary /></UserDashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/library/:contentId"
            element={
              <ProtectedRoute>
                <UserDashboardWrapper><ContentDetail /></UserDashboardWrapper>
              </ProtectedRoute>
            }
          />

          {/* Protected Admin Routes */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute adminOnly>
                <AdminDashboardWrapper><AdminDashboard /></AdminDashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/prompts"
            element={
              <ProtectedRoute adminOnly>
                <AdminDashboardWrapper><AdminPrompts /></AdminDashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/bookings"
            element={
              <ProtectedRoute adminOnly>
                <AdminDashboardWrapper><AdminBookings /></AdminDashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/applications"
            element={
              <ProtectedRoute adminOnly>
                <AdminDashboardWrapper><AdminApplications /></AdminDashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/email-templates"
            element={
              <ProtectedRoute adminOnly>
                <AdminDashboardWrapper><AdminEmailTemplates /></AdminDashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/pdf-templates"
            element={
              <ProtectedRoute adminOnly>
                <AdminDashboardWrapper><AdminPdfTemplates /></AdminDashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/email-sequences"
            element={
              <ProtectedRoute adminOnly>
                <AdminDashboardWrapper><AdminEmailSequences /></AdminDashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/email-logs"
            element={
              <ProtectedRoute adminOnly>
                <AdminDashboardWrapper><AdminEmailLogs /></AdminDashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/participants"
            element={
              <ProtectedRoute adminOnly>
                <AdminDashboardWrapper><AdminParticipants /></AdminDashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/participants/:userId"
            element={
              <ProtectedRoute adminOnly>
                <AdminDashboardWrapper><AdminParticipantDetail /></AdminDashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/flagged-sessions"
            element={
              <ProtectedRoute adminOnly>
                <AdminDashboardWrapper><AdminFlaggedSessions /></AdminDashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/session-templates"
            element={
              <ProtectedRoute adminOnly>
                <AdminDashboardWrapper><AdminSessionTemplates /></AdminDashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/reports"
            element={
              <ProtectedRoute adminOnly>
                <AdminDashboardWrapper><AdminReports /></AdminDashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/cohort-comparison"
            element={
              <ProtectedRoute adminOnly>
                <AdminDashboardWrapper><AdminCohortComparison /></AdminDashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/participants/:userId/journey"
            element={
              <ProtectedRoute adminOnly>
                <AdminDashboardWrapper><AdminParticipantJourney /></AdminDashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <ProtectedRoute adminOnly>
                <AdminDashboardWrapper><AdminSettings /></AdminDashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/ai-insights"
            element={
              <ProtectedRoute adminOnly>
                <AdminDashboardWrapper><AdminAIInsights /></AdminDashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/facilitator"
            element={
              <ProtectedRoute adminOnly>
                <FacilitatorDashboardWrapper><FacilitatorDashboard /></FacilitatorDashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/facilitator/session/:sessionId"
            element={
              <ProtectedRoute adminOnly>
                <FacilitatorDashboardWrapper><FacilitatedSession /></FacilitatorDashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/facilitator/analytics"
            element={
              <ProtectedRoute adminOnly>
                <FacilitatorDashboardWrapper><FacilitatorAnalytics /></FacilitatorDashboardWrapper>
              </ProtectedRoute>
            }
          />
          {/* Legacy routes - redirect to new paths */}
          <Route
            path="/admin/facilitator"
            element={
              <ProtectedRoute adminOnly>
                <FacilitatorDashboardWrapper><FacilitatorDashboard /></FacilitatorDashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/facilitator/session/:sessionId"
            element={
              <ProtectedRoute adminOnly>
                <FacilitatorDashboardWrapper><FacilitatedSession /></FacilitatorDashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/cohorts"
            element={
              <ProtectedRoute adminOnly>
                <AdminDashboardWrapper><AdminCohorts /></AdminDashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/cohorts/:cohortId"
            element={
              <ProtectedRoute adminOnly>
                <AdminDashboardWrapper><AdminCohortDetail /></AdminDashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/testimonials"
            element={
              <ProtectedRoute adminOnly>
                <AdminDashboardWrapper><AdminTestimonials /></AdminDashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/activity-logs"
            element={
              <ProtectedRoute adminOnly>
                <AdminDashboardWrapper><AdminActivityLogs /></AdminDashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/webhooks"
            element={
              <ProtectedRoute adminOnly>
                <AdminDashboardWrapper><AdminWebhooks /></AdminDashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/notifications"
            element={
              <ProtectedRoute adminOnly>
                <AdminDashboardWrapper><AdminNotifications /></AdminDashboardWrapper>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/content"
            element={
              <ProtectedRoute adminOnly>
                <AdminDashboardWrapper><AdminContentLibrary /></AdminDashboardWrapper>
              </ProtectedRoute>
            }
          />

          {/* Catch-all redirect */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
